import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  List
} from '@mui/material';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import {
  AccessTime,
  Person,
  Pets,
  Business,
  Warning,
  CheckCircle,
  Schedule,
  Event,
  ArrowBack,
  ArrowForward,
  Today,
  Add,
  Print,
  Download,
  Search,
  Refresh,
  ExpandMore,
  ExpandLess,
  Edit,
  Delete,
  MoreVert,
  DragIndicator,
  Clear,
  Save
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import './FullCalendarStyles.css';
import { Appointment } from '@services/appointmentsService';
import { accessTokensService } from '@services/accessTokensService';
import { exportService } from '@services/exportService';
import { appointmentsService } from '@services/appointmentsService';
import { visitsService, Visit } from '@services/visitsService';
import { useNavigate } from 'react-router-dom';

// Debug flag for development
const DEBUG = process.env.NODE_ENV === 'development';

interface FullCalendarWeekViewProps {
  appointments: Appointment[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onAppointmentClick: (appointment: Appointment) => void;
  onAddAppointment: () => void;
  onAppointmentUpdate?: (appointment: Appointment) => void;
  onRefresh?: () => void;
  veterinarians?: Array<{ id: string; title: string; color?: string }>;
}


const FullCalendarWeekView: React.FC<FullCalendarWeekViewProps> = ({
  appointments,
  selectedDate,
  onDateChange,
  onAppointmentClick,
  onAddAppointment,
  onAppointmentUpdate,
  onRefresh,
  veterinarians = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const calendarRef = useRef<FullCalendar>(null);
  const navigate = useNavigate();
  
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('no_show');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<string>('');
  const [rescheduleTime, setRescheduleTime] = useState<string>('');
  const [rescheduleReason, setRescheduleReason] = useState<string>('');
  const [notifyClient, setNotifyClient] = useState<boolean>(true);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'list'>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [moveVetId, setMoveVetId] = useState<string>('');
  const [lastVisit, setLastVisit] = useState<Visit | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [qrToken, setQrToken] = useState<string>('');
  const [qrValidated, setQrValidated] = useState<boolean>(false);
  const requireQRGate = (localStorage.getItem('requireQRForVisitStart') || '').toLowerCase() === 'true';





  // Convert appointments to calendar events
  const normalizeEventDuration = (start: Date, end?: Date | null, fallbackMinutes = 30) => {
    const hasEnd = !!end;
    const dur = hasEnd ? ((end!.getTime() - start.getTime()) / 60000) : 0;
    if (!hasEnd || dur < 10) {
      return new Date(start.getTime() + fallbackMinutes * 60000);
    }
    return end as Date;
  };

  const events = useMemo(() => {
    console.log('🔄 Converting appointments to events:', appointments.length);
    return appointments.map(appointment => {
      const start = new Date(appointment.date + 'T' + appointment.time);
      const end = normalizeEventDuration(start, new Date(start.getTime() + (appointment.duration || 30) * 60000));
      
      return {
        id: appointment.id,
        title: `${appointment.patientName} - ${appointment.clientName}`,
        start,
        end,
        extendedProps: {
          appointment,
          patientName: appointment.patientName,
          clientName: appointment.clientName,
          serviceName: appointment.serviceName,
          animalSpecies: appointment.animalSpecies,
          status: appointment.status,
          duration: appointment.duration || 30,
          priority: appointment.priority || 'normal',
          veterinarianName: appointment.veterinarianName,
          veterinarianId: appointment.veterinarianId
        }
      };
    });
  }, [appointments]);

  // Note: Resources removed to avoid dependency issues

  // Status colors and labels
  const getStatusConfig = (status: string) => {
    const configs = {
      'scheduled': { color: '#2196F3', label: 'Programat', borderColor: '#1976D2' },
      'confirmed': { color: '#4CAF50', label: 'Confirmat', borderColor: '#388E3C' },
      'present': { color: '#8BC34A', label: 'Prezent', borderColor: '#689F38' }, // legacy
      'arrived': { color: '#8BC34A', label: 'Prezent', borderColor: '#689F38' },
      'in_progress': { color: '#FF9800', label: 'În desfășurare', borderColor: '#F57C00' }, // legacy
      'in-progress': { color: '#FF9800', label: 'În desfășurare', borderColor: '#F57C00' },
      'completed': { color: '#4CAF50', label: 'Finalizat', borderColor: '#388E3C' },
      'cancelled': { color: '#F44336', label: 'Anulat', borderColor: '#D32F2F' },
      'no_show': { color: '#9E9E9E', label: 'No-show', borderColor: '#616161' },
      'rescheduled': { color: '#9C27B0', label: 'Reprogramat', borderColor: '#7B1FA2' }
    };
    
    return configs[status as keyof typeof configs] || configs['scheduled'];
  };

  // Get veterinarian color
  const getVeterinarianColor = (name: string | undefined) => {
    if (!name) return '#2196F3';
    const colors = [
      '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336',
      '#00BCD4', '#8BC34A', '#FF5722', '#607D8B', '#795548'
    ];
    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  // Event content renderer – cu icon status și text mai mare
  const renderEventContent = (eventInfo: any) => {
    const ext = eventInfo.event.extendedProps || {};
    const statusCfg = getStatusConfig(ext.status || 'scheduled');
    const statusColor = statusCfg.borderColor;
    const serviceName = ext.serviceName || '';
    const veterinarianName = ext.veterinarianName || '';
    const patientName = ext.patientName || '';
    const clientName = ext.clientName || '';
    
    if (DEBUG) {
      console.log('🔍 renderEventContent - ext:', ext);
      console.log('🔍 renderEventContent - eventInfo.event.title:', eventInfo.event.title);
      console.log('🔍 renderEventContent - patientName:', patientName);
      console.log('🔍 renderEventContent - veterinarianName:', veterinarianName);
      console.log('🔍 renderEventContent - clientName:', clientName);
    }
    
    // Icon status - simboluri clare
    const statusIcon = ext.status === 'confirmed' ? '✓' : 
                       ext.status === 'present' ? '●' : 
                       ext.status === 'rescheduled' ? '↻' : 
                       ext.status === 'cancelled' ? '✕' : 
                       ext.status === 'completed' ? '✓✓' : 
                       ext.status === 'no_show' ? '⊘' : '○';
    
    // Build display text with all available info
    const ownerName = clientName || ext.ownerName || '';
    const animalName = patientName || ext.animalName || '';
    const doctorName = veterinarianName || ext.doctorName || '';
    
    // Primary line: Animal (Owner)
    const primaryText = animalName && ownerName 
      ? `${animalName} (${ownerName})`
      : animalName || ownerName || eventInfo.event.title || 'Programare';
    
    // Secondary line: Doctor or Service  
    const secondaryText = doctorName || serviceName;
    
    // Return React element with complete info and better layout
    return React.createElement('div', {
      style: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        borderLeft: `4px solid ${statusColor}`,
        paddingLeft: '6px',
        paddingRight: '4px',
        paddingTop: '3px',
        paddingBottom: '3px',
        fontSize: '13px',
        fontWeight: '500',
        background: '#fff',
        height: '100%',
        color: '#1a1a1a',
        overflow: 'hidden'
      }
    }, [
      // Time + Status Icon
      React.createElement('div', {
        key: 'time',
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          color: '#666',
          fontWeight: '600'
        }
      }, [
        React.createElement('span', { key: 'icon', style: { fontSize: '12px' } }, statusIcon),
        React.createElement('span', { key: 'timetext' }, eventInfo.timeText)
      ]),
      // Primary: Animal (Owner)
      React.createElement('div', {
        key: 'primary',
        style: { 
          fontSize: '13px',
          fontWeight: '600',
          color: '#1a1a1a',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: '1.3'
        }
      }, primaryText),
      // Secondary: Doctor or Service
      secondaryText && React.createElement('div', {
        key: 'secondary',
        style: { 
          fontSize: '11px',
          fontWeight: '400',
          color: '#666',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: '1.3'
        }
      }, secondaryText)
    ].filter(Boolean));
  };

  // Tooltip compus din titlu + serviciu + telefon (dacă există)
  // Ensure tippy CSS is present (loaded once)
  const ensureTippyCssLoaded = () => {
    const id = 'tippy-light-border-css';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/tippy.js@6/themes/light-border.css';
      document.head.appendChild(link);
    }
    const baseId = 'tippy-base-css';
    if (!document.getElementById(baseId)) {
      const link = document.createElement('link');
      link.id = baseId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/tippy.js@6/dist/tippy.css';
      document.head.appendChild(link);
    }
  };

  const handleEventDidMount = (info: any) => {
    const ext = info.event.extendedProps || {};
    
    // Extract all available info
    const patientName = ext.patientName || ext.animalName || '';
    const ownerName = ext.clientName || ext.ownerName || '';
    const doctorName = ext.veterinarianName || ext.doctorName || '';
    const serviceName = ext.serviceName || '';
    const statusLabel = ext.status || 'scheduled';
    const phone = ext.clientPhone || ext.phone || '';
    const notes = ext.notes || '';
    
    // Build rich tooltip content
    const content = `
      <div style="padding: 4px;">
        <div style="font-weight: 600; font-size: 14px; margin-bottom: 6px; color: #1a1a1a;">
          🐾 ${patientName || 'Necunoscut'}
        </div>
        ${ownerName ? `<div style="margin-bottom: 4px;">👤 Proprietar: <strong>${ownerName}</strong></div>` : ''}
        ${doctorName ? `<div style="margin-bottom: 4px;">🩺 Medic: <strong>${doctorName}</strong></div>` : ''}
        ${serviceName ? `<div style="margin-bottom: 4px;">💉 Serviciu: ${serviceName}</div>` : ''}
        ${phone ? `<div style="margin-bottom: 4px;">📞 Tel: ${phone}</div>` : ''}
        <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #eee;">
          <span style="font-size: 12px; color: #666;">Status: <strong>${statusLabel}</strong></span>
        </div>
        ${notes ? `<div style="margin-top: 4px; font-size: 12px; color: #666; font-style: italic;">${notes}</div>` : ''}
      </div>
    `;
    
    // Fallback title for native tooltip
    const fallbackTitle = `${patientName} - ${ownerName} • ${doctorName} • ${serviceName}`;
    info.el.title = fallbackTitle;
    
    // Try to load tippy for rich tooltip
    ensureTippyCssLoaded();
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const tippy = require('tippy.js').default;
      tippy(info.el, {
        content,
        allowHTML: true,
        theme: 'light-border',
        delay: [200, 0],
        placement: 'top',
        interactive: true,
        maxWidth: 350
      });
    } catch {
      // fallback: title nativ deja setat
    }
  };

  // Handle event click
  const handleEventClick = (clickInfo: any) => {
    const appointment = clickInfo.event.extendedProps.appointment;
    setSelectedAppointment(appointment);
    setAppointmentDetailsOpen(true);
  };

  // Load last visit when details open
  useEffect(() => {
    const loadLastVisit = async () => {
      try {
        if (!detailsOpen || !(selectedAppointment as any)?.patientId) { setLastVisit(null); return; }
        const petId = String((selectedAppointment as any).patientId);
        const visits = await visitsService.getVisitsForPet(petId);
        const sorted = [...visits].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
        setLastVisit(sorted[0] || null);
      } catch (e: any) {
        if (!(e && e.response && e.response.status === 404)) {
          console.error('Eroare la încărcarea ultimei vizite:', e);
        }
        setLastVisit(null);
      }
    };
    loadLastVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen, selectedAppointment]);

  // Handle date select
  const handleDateSelect = (selectInfo: any) => {
    // Create new appointment at selected time
    onAddAppointment();
  };

  // Handle event drop
  const handleEventDrop = (dropInfo: any) => {
    const appointment = dropInfo.event.extendedProps.appointment;
    const newStart = dropInfo.event.start;
    const newEnd = dropInfo.event.end;
    const newResourceId = dropInfo.event.getResources()[0]?.id;
    
    // Check for double booking conflicts with buffer
    const BUFFER_MINUTES = 10; // 10 minute buffer between appointments
    const conflicts = appointments.filter(apt => {
      if (apt.id === appointment.id) return false;
      
      const aptStart = new Date(apt.date + 'T' + apt.time);
      const aptEnd = new Date(aptStart.getTime() + (apt.duration || 30) * 60000);
      
      // Check if same veterinarian and overlapping time with buffer
      const sameVet = (apt as any).veterinarianId === newResourceId || 
                     apt.veterinarianName === appointment.veterinarianName;
      
      if (!sameVet) return false;
      
      // Add buffer time
      const newStartWithBuffer = new Date(newStart.getTime() - BUFFER_MINUTES * 60000);
      const newEndWithBuffer = new Date(newEnd.getTime() + BUFFER_MINUTES * 60000);
      
      return (newStartWithBuffer < aptEnd && newEndWithBuffer > aptStart);
    });
    
    if (conflicts.length > 0) {
      alert(`Conflict: Există deja o programare în această perioadă pentru același medic! Buffer minim: ${BUFFER_MINUTES} minute.`);
      dropInfo.revert();
      return;
    }
    
    // Update appointment with new time and veterinarian
    if (onAppointmentUpdate) {
      const updatedAppointment = {
        ...appointment,
        date: newStart.toISOString().split('T')[0],
        time: newStart.toTimeString().split(' ')[0].substring(0, 5),
        duration: Math.round((newEnd.getTime() - newStart.getTime()) / (1000 * 60)),
        ...(newResourceId && { veterinarianId: newResourceId })
      };
      onAppointmentUpdate(updatedAppointment);
    }
  };

  // Handle event resize
  const handleEventResize = (resizeInfo: any) => {
    const appointment = resizeInfo.event.extendedProps.appointment;
    const newEnd = resizeInfo.event.end;
    const newStart = resizeInfo.event.start;
    const newDuration = Math.round((newEnd.getTime() - newStart.getTime()) / (1000 * 60));
    
    // Check for conflicts with new duration and buffer
    const BUFFER_MINUTES = 10; // 10 minute buffer between appointments
    const conflicts = appointments.filter(apt => {
      if (apt.id === appointment.id) return false;
      
      const aptStart = new Date(apt.date + 'T' + apt.time);
      const aptEnd = new Date(aptStart.getTime() + (apt.duration || 30) * 60000);
      
      // Check if same veterinarian and overlapping time with buffer
      const sameVet = (apt as any).veterinarianId === appointment.veterinarianId || 
                     apt.veterinarianName === appointment.veterinarianName;
      
      if (!sameVet) return false;
      
      // Add buffer time
      const newStartWithBuffer = new Date(newStart.getTime() - BUFFER_MINUTES * 60000);
      const newEndWithBuffer = new Date(newEnd.getTime() + BUFFER_MINUTES * 60000);
      
      return (newStartWithBuffer < aptEnd && newEndWithBuffer > aptStart);
    });
    
    if (conflicts.length > 0) {
      alert(`Conflict: Durata programării se suprapune cu alte programări! Buffer minim: ${BUFFER_MINUTES} minute.`);
      resizeInfo.revert();
      return;
    }
    
    // Update appointment with new duration
    if (onAppointmentUpdate) {
      const updatedAppointment = {
        ...appointment,
        duration: newDuration
      };
      onAppointmentUpdate(updatedAppointment);
    }
  };

  // Navigation handlers
  const handlePrev = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.prev();
    }
  };

  const handleNext = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.next();
    }
  };

  const handleToday = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.today();
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const csvData = appointments.map(apt => ({
      Data: apt.date,
      Ora: apt.time,
      Pacient: apt.patientName,
      Proprietar: apt.clientName,
      Serviciu: apt.serviceName,
      Status: getStatusConfig(apt.status).label,
      Durata: `${apt.duration || 30} min`
    }));
    
    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `programari-${selectedDate.toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Speed dial actions
  const speedDialActions = [
    {
      icon: <Add />,
      name: 'Programare nouă',
      onClick: onAddAppointment
    },
    {
      icon: <Print />,
      name: 'Print',
      onClick: () => window.print()
    },
    {
      icon: <Download />,
      name: 'Export CSV',
      onClick: handleExportCSV
    }
  ];

  // Appointment details modal handlers
  const handleOpenAppointmentDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentDetailsOpen(true);
  };

  const handleCloseAppointmentDetails = () => {
    setAppointmentDetailsOpen(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentStatusChange = async (appointmentId: string, newStatus: string) => {
    try {
      const response = await fetch(`/api/v1/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Eroare la actualizarea statusului');
      }

      // Refresh appointments
      onRefresh?.();
      setSnackbar({ open: true, message: 'Status actualizat cu succes', severity: 'success' });
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error);
      setSnackbar({ open: true, message: 'Eroare la actualizarea statusului', severity: 'error' });
    }
  };

  const handleAppointmentReschedule = async (appointmentId: string, newStart: string, newEnd: string, note?: string) => {
    try {
      const response = await fetch(`/api/v1/appointments/${appointmentId}/reschedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          start: newStart, 
          end: newEnd, 
          note: note 
        })
      });

      if (!response.ok) {
        throw new Error('Eroare la reprogramare');
      }

      // Refresh appointments
      onRefresh?.();
      setSnackbar({ open: true, message: 'Programarea a fost reprogramată', severity: 'success' });
    } catch (error) {
      console.error('Eroare la reprogramare:', error);
      setSnackbar({ open: true, message: 'Eroare la reprogramare', severity: 'error' });
    }
  };

  const handleAppointmentCancel = async (appointmentId: string, notifyOwner: boolean, reason?: string) => {
    try {
      const response = await fetch(`/api/v1/appointments/${appointmentId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          notifyOwner: notifyOwner,
          reason: reason 
        })
      });

      if (!response.ok) {
        throw new Error('Eroare la anulare');
      }

      // Refresh appointments
      onRefresh?.();
      setSnackbar({ open: true, message: 'Programarea a fost anulată', severity: 'success' });
    } catch (error) {
      console.error('Eroare la anulare:', error);
      setSnackbar({ open: true, message: 'Eroare la anulare', severity: 'error' });
    }
  };

  const handleAppointmentAddNote = async (appointmentId: string, note: string) => {
    try {
      const response = await fetch(`/api/v1/appointments/${appointmentId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ note: note })
      });

      if (!response.ok) {
        throw new Error('Eroare la adăugarea notiței');
      }

      setSnackbar({ open: true, message: 'Notița a fost adăugată', severity: 'success' });
    } catch (error) {
      console.error('Eroare la adăugarea notiței:', error);
      setSnackbar({ open: true, message: 'Eroare la adăugarea notiței', severity: 'error' });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <Box sx={{ flex: 1, minHeight: 600 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: ''
          }}
          buttonText={{ week: 'Saptamana', day: 'Zi', list: 'Agenda' }}
          locale={'ro'}
          weekNumbers={false}
          firstDay={1}
          titleFormat={{ year: 'numeric', month: 'long', day: 'numeric' }}
          dayHeaderClassNames={(arg: any) => (arg.isToday ? ['is-today'] : [])}
          dayCellClassNames={(arg: any) => (arg.isPast ? ['is-past'] : [])}
          slotDuration={'00:30:00'}
          slotLabelInterval={{ hour: 1 }}
          slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          slotMinTime={'08:00:00'}
          slotMaxTime={'20:00:00'}
          scrollTime={'08:00:00'}
          expandRows={true}
          nowIndicator={true}
          snapDuration={'00:10:00'}
          eventOrder={'start, status'}
          slotEventOverlap={false}
          eventDisplay={'block'}
          displayEventTime={true}
          displayEventEnd={true}
          eventOverlap={false}
          allDaySlot={false}
          eventsSet={function(this: any){
            const hasAllDay = this.getEvents().some((e: any) => e.allDay);
            this.setOption('allDaySlot', hasAllDay);
          }}
          events={events}
          eventContent={renderEventContent}
          eventClassNames={(eventInfo) => {
            const status = eventInfo.event.extendedProps?.status || 'scheduled';
            return [`status-${status}`, 'custom-event'];
          }}
          eventDidMount={handleEventDidMount}
          eventClick={handleEventClick}
          select={handleDateSelect}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          selectable={true}
          editable={true}
          droppable={true}
          height="auto"
          // Accessibility improvements
        />
      </Box>

      {/* Speed Dial - Doar pe mobil/tablet */}
      <SpeedDial
        ariaLabel="Calendar actions"
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16,
          display: { xs: 'flex', lg: 'none' } // Vizibil doar sub 1024px
        }}
        icon={<SpeedDialIcon />}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
        open={speedDialOpen}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={() => {
              action.onClick();
              setSpeedDialOpen(false);
            }}
          />
        ))}
      </SpeedDial>

      {/* Appointment Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth onKeyDown={(e) => {
        if (e.key === 'Escape') { setDetailsOpen(false); }
        if (e.key === 'Enter') {
          if (rescheduleDialogOpen) {
            const btn = document.getElementById('reschedule-save-btn');
            if (btn) (btn as HTMLButtonElement).click();
          } else if (confirmCancelOpen) {
            const btn = document.getElementById('cancel-confirm-btn');
            if (btn) (btn as HTMLButtonElement).click();
          }
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e') {
          if (selectedAppointment) { onAppointmentClick(selectedAppointment); setDetailsOpen(false); }
        }
      }}>
        <DialogTitle>
          {selectedAppointment ? `Detalii programare — ${selectedAppointment.patientName} (${selectedAppointment.animalSpecies || ''})` : 'Detalii programare'}
          <Typography variant="body2" color="text.secondary">
            {selectedAppointment ? `${selectedAppointment.date}, ${selectedAppointment.time}–${(() => {
              try {
                const [h, m] = (selectedAppointment.time || '00:00').split(':').map(Number);
                const d = new Date(); d.setHours(h, m, 0, 0);
                const end = new Date(d.getTime() + (selectedAppointment.duration || 30) * 60000);
                const hh = String(end.getHours()).padStart(2, '0');
                const mm = String(end.getMinutes()).padStart(2, '0');
                return `${hh}:${mm}`;
              } catch { return ''; }
            })()} • Durată ${(selectedAppointment?.duration || 30)}’` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 1.5fr 1fr' }, gap: 2 }}>
              {/* Stânga: pacient & proprietar, atenționări */}
            <Box>
                <Typography variant="subtitle2" color="text.secondary">Pacient</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.patientName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAppointment.animalSpecies || '-'}{((selectedAppointment as any).animalBreed) ? ` / ${String((selectedAppointment as any).animalBreed)}` : ''}{((selectedAppointment as any).animalGender) ? ` / ${String((selectedAppointment as any).animalGender)}` : ''}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  {((selectedAppointment as any).animalAge) ? `Vârstă: ${String((selectedAppointment as any).animalAge)}` : ''}{((selectedAppointment as any).animalWeight) ? ` • Greutate: ${String((selectedAppointment as any).animalWeight)} kg` : ''}
                  </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" color="text.secondary">Proprietar</Typography>
                <Typography variant="body1">{selectedAppointment.clientName}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {selectedAppointment.clientPhone && (
                    <Tooltip title="Apelează">
                      <IconButton component="a" href={`tel:${selectedAppointment.clientPhone}`} aria-label="apeleaza" size="small">📞</IconButton>
                    </Tooltip>
                  )}
                  {selectedAppointment.clientPhone && (
                    <Tooltip title="WhatsApp">
                      <IconButton component="a" href={`https://wa.me/${selectedAppointment.clientPhone.replace(/\D/g, '')}`} target="_blank" rel="noopener" aria-label="whatsapp" size="small">💬</IconButton>
                    </Tooltip>
                  )}
                </Stack>

                {(Array.isArray((selectedAppointment as any).allergies) && (selectedAppointment as any).allergies.length > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Alergii / atenționări</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(selectedAppointment as any).allergies.map((a: string, idx: number) => (
                        <Chip key={idx} label={a} color="warning" size="small" />
                      ))}
                      {Array.isArray((selectedAppointment as any).flags) && (selectedAppointment as any).flags.map((f: string, idx: number) => (
                        <Chip key={`f-${idx}`} label={f} color="error" size="small" />
                      ))}
                    </Stack>
                </Box>
                )}
                </Box>

              {/* Mijloc: detalii programare */}
                <Box>
                <Typography variant="subtitle2" color="text.secondary">Detalii programare</Typography>
                <List dense>
                  <li>
                    <Typography variant="body2">Serviciu: <strong>{selectedAppointment.serviceName}</strong></Typography>
                  </li>
                  {selectedAppointment.veterinarianName && (
                    <li>
                      <Typography variant="body2">Medic: <strong>{selectedAppointment.veterinarianName}</strong></Typography>
                    </li>
                  )}
                  {(selectedAppointment as any).roomName && (
                    <li>
                      <Typography variant="body2">Cabinet: <strong>{(selectedAppointment as any).roomName}</strong></Typography>
                    </li>
                  )}
                  {(selectedAppointment as any).locationName && (
                    <li>
                      <Typography variant="body2">Unitate: <strong>{(selectedAppointment as any).locationName}</strong></Typography>
                    </li>
                  )}
                  {selectedAppointment.reason && (
                    <li>
                      <Typography variant="body2">Motiv: <strong>{selectedAppointment.reason}</strong></Typography>
                    </li>
                  )}
                  {(selectedAppointment as any).notes && (
                    <li>
                      <Typography variant="body2">Notițe: {(selectedAppointment as any).notes}</Typography>
                    </li>
                  )}
                </List>

                {/* Istoric scurt */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">Istoric scurt</Typography>
                  {lastVisit ? (
                    <Typography variant="body2">
                      Ultima vizită: {new Date(lastVisit.startTime).toLocaleString('ro-RO')} — {lastVisit.reason || 'Consultație'}
                  </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nu există vizite anterioare.</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">Următoare scadențe: vaccinuri/rapeluri (în curând)</Typography>
                </Box>

                {/* Câmpuri vaccin (dacă serviciul este vaccinare) */}
                {((selectedAppointment.serviceName || '').trim().toLowerCase() === 'vaccinare' || (selectedAppointment as any).isVaccination === true) && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">Vaccinare</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                      <TextField size="small" label="Serie lot" placeholder="Completați în consultație" fullWidth />
                      <TextField size="small" label="Expiră la" placeholder="ZZ/LL/AAAA" fullWidth />
                      <TextField size="small" label="Producător" placeholder="—" fullWidth />
                    </Stack>
                </Box>
                )}

                {/* Status badge + timestamps */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Chip label={getStatusConfig(selectedAppointment.status).label} sx={{ bgcolor: getStatusConfig(selectedAppointment.status).color, color: '#fff' }} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {selectedAppointment.createdAt ? `Creat: ${new Date(selectedAppointment.createdAt).toLocaleString('ro-RO')}` : ''}
                  </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {selectedAppointment.updatedAt ? `Ultima modificare: ${new Date(selectedAppointment.updatedAt).toLocaleString('ro-RO')}` : ''}
                  </Typography>
                </Box>

              {/* Dreapta: acțiuni */}
                <Box>
                <Typography variant="subtitle2" color="text.secondary">Acțiuni rapide</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Button variant="contained" onClick={async () => {
                    try {
                      // QR Gating check
                      if (requireQRGate && !qrValidated) {
                        setSnackbar({ open: true, message: 'Validarea QR este obligatorie pentru pornirea consultației', severity: 'error' });
                        return;
                      }

                      // Creează sau obține vizita existentă pentru ACEASTĂ programare
                      let visitId: string | null = null;
                      try {
                        const result = await appointmentsService.convertToVisit(selectedAppointment.id);
                        visitId = result.visitId;
                        
                        if (!visitId) {
                          setSnackbar({ open: true, message: 'Nu am putut crea vizita', severity: 'error' });
                          return;
                        }
                      } catch (err: any) {
                        console.error('Error converting appointment to visit:', err);
                        setSnackbar({ open: true, message: 'Eroare la crearea vizitei', severity: 'error' });
                        return;
                      }

                      // 3) Pornire consultație (în fundal, nu blocăm navigarea dacă backend-ul dă 500)
                      appointmentsService.startAppointment(selectedAppointment.id)
                        .then(() => onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'in-progress' } as any))
                        .catch(() => setSnackbar({ open: true, message: 'Atenție: vizita există, dar startul a eșuat', severity: 'info' }));

                      setSnackbar({ open: true, message: 'Consultația a fost deschisă', severity: 'success' });
                      setDetailsOpen(false);
                      navigate(`/visits/${visitId}/encounter`);
                    } catch (e) {
                      setSnackbar({ open: true, message: 'Eroare la deschiderea consultației', severity: 'error' });
                    }
                  }} disabled={requireQRGate && !qrValidated}>
                    Deschide consultația
                  </Button>

                  {/* QR Validation UI */}
                  {requireQRGate && !qrValidated && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>⚠️ QR validare obligatorie:</Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="Token QR"
                          value={qrToken}
                          onChange={(e) => setQrToken(e.target.value)}
                          placeholder="Scanează sau introdu token"
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={async () => {
                            try {
                              const result = await accessTokensService.redeem(qrToken);
                              if (result && (result as any).valid) {
                                setQrValidated(true);
                                setSnackbar({ open: true, message: 'QR validat cu succes', severity: 'success' });
                              } else {
                                setSnackbar({ open: true, message: 'Token QR invalid', severity: 'error' });
                              }
                            } catch (e) {
                              setSnackbar({ open: true, message: 'Token QR invalid sau expirat', severity: 'error' });
                            }
                          }}
                          disabled={!qrToken}
                        >
                          Verifică
                        </Button>
                      </Stack>
                    </Box>
                  )}
                  {/* Status transitions */}
                  {selectedAppointment.status === 'scheduled' && (
                    <Button variant="outlined" onClick={() => { 
                      onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'confirmed' } as any); 
                      setSnackbar({ open: true, message: 'Programare confirmată', severity: 'success' });
                      setDetailsOpen(false); 
                    }}>Confirmă</Button>
                  )}
                  {(selectedAppointment.status === 'confirmed' || selectedAppointment.status === 'scheduled') && (
                    <Button variant="outlined" onClick={async () => { 
                      try {
                        await (appointmentsService as any).checkIn(selectedAppointment.id);
                        onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'arrived' } as any);
                        setSnackbar({ open: true, message: 'Check-in efectuat', severity: 'success' });
                        setDetailsOpen(false);
                      } catch {
                        setSnackbar({ open: true, message: 'Eroare la check-in', severity: 'error' });
                      }
                    }}>Check-in</Button>
                  )}
                  {(selectedAppointment.status === 'arrived' || selectedAppointment.status === 'confirmed') && (
                    <Button variant="outlined" onClick={async () => { 
                      try {
                        await appointmentsService.startAppointment(selectedAppointment.id);
                        onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'in-progress' } as any);
                        setSnackbar({ open: true, message: 'Marcat În consultație', severity: 'success' });
                        setDetailsOpen(false);
                      } catch {
                        setSnackbar({ open: true, message: 'Eroare la pornirea consultației', severity: 'error' });
                      }
                    }}>În consultație</Button>
                  )}
                  {(selectedAppointment.status === 'in-progress') && (
                    <Button variant="contained" color="success" onClick={() => { 
                      onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'completed' } as any); 
                      setSnackbar({ open: true, message: 'Programare finalizată', severity: 'success' });
                      setDetailsOpen(false); 
                    }}>Check-out / Finalizează</Button>
                  )}
                  <Button variant="outlined" disabled={['arrived','in-progress','completed'].includes(selectedAppointment.status as any)} onClick={() => { setRescheduleDialogOpen(true); }}>Reprogramează</Button>
                  <Button variant="outlined" disabled={['arrived','in-progress','completed'].includes(selectedAppointment.status as any)} onClick={() => { setConfirmCancelOpen(true); }}>Anulează</Button>
                  <Button variant="text" onClick={async () => {
                    try {
                      const petId = String((selectedAppointment as any).patientId || '');
                      if (!petId) {
                        setSnackbar({ open: true, message: 'Nu există pacient asociat', severity: 'error' });
                        return;
                      }
                      const blob = await exportService.exportPetPDF(petId, {
                        scope: 'visits',
                        includeIdentity: true,
                        includeVisits: true,
                        includeVaccinations: true,
                        includePrescriptions: true,
                        includeDocuments: false
                      });
                      await exportService.downloadPDF(blob, `fisa_${petId}.pdf`);
                    } catch (e) {
                      setSnackbar({ open: true, message: 'Eroare la export PDF', severity: 'error' });
                    }
                  }}>Imprimă / Export PDF</Button>
                  {/* Mută la alt medic */}
                  {veterinarians && veterinarians.length > 0 && (
                    <FormControl size="small" fullWidth>
                      <InputLabel id="move-vet-label">Mută la alt medic</InputLabel>
                      <Select labelId="move-vet-label" label="Mută la alt medic" value={moveVetId} onChange={(e) => setMoveVetId(String(e.target.value))}>
                        {veterinarians.map(v => (
                          <MenuItem key={v.id} value={v.id}>{v.title}</MenuItem>
                        ))}
                      </Select>
                      <Button sx={{ mt: 1 }} variant="outlined" disabled={!moveVetId} onClick={() => {
                        if (onAppointmentUpdate && selectedAppointment && moveVetId) {
                          onAppointmentUpdate({ ...selectedAppointment, veterinarianId: moveVetId } as any);
                          setSnackbar({ open: true, message: 'Programare mutată la alt medic', severity: 'success' });
                          setDetailsOpen(false);
                        }
                      }}>Mută</Button>
                    </FormControl>
                  )}
                  {/* Mesaje rapide */}
                  {selectedAppointment.clientPhone && (/^\+?\d{7,15}$/.test(String(selectedAppointment.clientPhone).replace(/\s|-/g, ''))) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="text" component="a" href={`tel:${selectedAppointment.clientPhone}`}>📞 Sună</Button>
                      <Button size="small" variant="text" component="a" href={`sms:${selectedAppointment.clientPhone}?&body=${encodeURIComponent('Bună ziua! Vă reamintim programarea pentru ' + (selectedAppointment.patientName || 'pacient') + ' la ora ' + (selectedAppointment.time || '') + '.')}`}>🧾 SMS</Button>
                      <Button size="small" variant="text" component="a" href={`https://wa.me/${selectedAppointment.clientPhone.replace(/\D/g, '')}?text=${encodeURIComponent('Bună ziua! Vă reamintim programarea pentru ' + (selectedAppointment.patientName || 'pacient') + ' la ora ' + (selectedAppointment.time || '') + '.')}`} target="_blank" rel="noopener">💬 WhatsApp</Button>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Închide</Button>
          <Button variant="outlined" disabled={['in-progress','completed','cancelled'].includes((selectedAppointment?.status || '') as string)} onClick={() => { onAddAppointment(); }}>Editează</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm cancel dialog */}
      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Anulează programarea</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>Motiv anulare:</Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="cancel-reason-label">Motiv</InputLabel>
            <Select
              labelId="cancel-reason-label"
              value={cancelReason}
              label="Motiv"
              onChange={(e) => setCancelReason(String(e.target.value))}
            >
              <MenuItem value="no_show">No-show</MenuItem>
              <MenuItem value="patient_sick">Pacient bolnav</MenuItem>
              <MenuItem value="duplicate">Dublură</MenuItem>
              <MenuItem value="other">Alt motiv</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} />} label="Notifică clientul automat" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)}>Renunță</Button>
          <Button id="cancel-confirm-btn" color="error" variant="contained" onClick={async () => {
            try {
            if (selectedAppointment) {
                await appointmentsService.cancelAppointment(selectedAppointment.id, cancelReason, false);
                onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, status: 'cancelled' } as any);
            }
              setSnackbar({ open: true, message: 'Programare anulată', severity: 'success' });
            } catch (e) {
              setSnackbar({ open: true, message: 'Eroare la anulare', severity: 'error' });
            }
            setConfirmCancelOpen(false);
            setDetailsOpen(false);
          }}>Confirmă anularea</Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reprogramează</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField type="date" label="Data" InputLabelProps={{ shrink: true }} value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} />
            <TextField type="time" label="Ora" InputLabelProps={{ shrink: true }} value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} />
            <TextField label="Motiv" value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} />
            <FormControlLabel control={<Checkbox checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} />} label="Notifică clientul automat" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)}>Renunță</Button>
          <Button id="reschedule-save-btn" variant="contained" onClick={async () => {
            if (!selectedAppointment) { setRescheduleDialogOpen(false); return; }
            const newDate = rescheduleDate || selectedAppointment.date;
            const newTime = rescheduleTime || selectedAppointment.time || '09:00';
            if (!newDate || !newTime) {
              setSnackbar({ open: true, message: 'Data și ora sunt obligatorii', severity: 'error' });
              return;
            }
            try {
              await appointmentsService.rescheduleAppointment(selectedAppointment.id, newDate, newTime, rescheduleReason || '');
              onAppointmentUpdate && onAppointmentUpdate({ ...selectedAppointment, date: newDate, time: newTime } as any);
              setSnackbar({ open: true, message: 'Programare reprogramată', severity: 'success' });
            } catch (e) {
              setSnackbar({ open: true, message: 'Eroare la reprogramare', severity: 'error' });
            }
            setRescheduleDialogOpen(false);
            setDetailsOpen(false);
          }}>Salvează</Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Details Modal */}
      {selectedAppointment && (
        <AppointmentDetailsModal
          open={appointmentDetailsOpen}
          onClose={handleCloseAppointmentDetails}
          appointment={{
            id: selectedAppointment.id,
            start: `${selectedAppointment.date}T${selectedAppointment.time}`,
            end: `${selectedAppointment.date}T${selectedAppointment.time}`,
            service: selectedAppointment.serviceName || 'Serviciu',
            reason: selectedAppointment.reason || 'Consultație',
            status: selectedAppointment.status as any,
            createdAt: selectedAppointment.createdAt || new Date().toISOString(),
            location: (selectedAppointment as any).location,
            patient: {
              id: selectedAppointment.patientId || '',
              name: selectedAppointment.patientName || 'Pacient',
              species: (selectedAppointment as any).animalSpecies || (selectedAppointment as any).species || 'necunoscut',
              breed: (selectedAppointment as any).breed || 'necunoscut',
              sex: (selectedAppointment as any).sex || 'necunoscut',
              weight: (selectedAppointment as any).weight,
              photoUrl: (selectedAppointment as any).photoUrl,
              birthDate: (selectedAppointment as any).birthDate,
              microchip: (selectedAppointment as any).microchip,
              sterilized: (selectedAppointment as any).sterilized,
              allergies: (selectedAppointment as any).allergies || [],
              aggressive: (selectedAppointment as any).aggressive,
              warnings: (selectedAppointment as any).warnings || []
            },
            owner: {
              id: selectedAppointment.clientId || '',
              name: selectedAppointment.clientName || 'Proprietar',
              phone: selectedAppointment.clientPhone
            },
            doctor: {
              id: selectedAppointment.veterinarianId || '',
              name: selectedAppointment.veterinarianName || 'Medic'
            },
            lastVisit: lastVisit ? {
              date: lastVisit.createdAt,
              type: (lastVisit as any).type || 'Consultație'
            } : undefined,
            nextDue: (selectedAppointment as any).nextDue
          }}
          onStatusChange={handleAppointmentStatusChange}
          onReschedule={handleAppointmentReschedule}
          onCancel={handleAppointmentCancel}
          onAddNote={handleAppointmentAddNote}
        />
      )}

      {/* Snackbar feedback */}
      {snackbar.open && (
        <Box
          role="alert"
          aria-live="polite"
          onAnimationEnd={() => undefined}
          sx={{ position: 'fixed', bottom: 16, left: 16, bgcolor: snackbar.severity === 'error' ? 'error.main' : snackbar.severity === 'info' ? 'info.main' : 'success.main', color: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: 3 }}
          onClick={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Box>
      )}
    </Box>
  );
};

export default FullCalendarWeekView;
